import CompanyLogo from '../company/CompanyLogo';
import UserBadge from '../profile/UserBadge';
import LanguageSelector from '../languages/LanguageSelector';
import useScreenType from '../../hooks/utility/useScreenType';
import CompanyDetailsModal from '../../modals/CompanyDetailsModal';
import { useMemo, useState } from 'react';
import useInitialData from '../../hooks/global/useInitialData';
import { Link } from 'react-router-dom';
import useRouter from '../../hooks/navigation/useRouter';
import useMenu from '../../hooks/menu/useMenu';
import { collect } from 'collect.js';
import { Drawer } from '@mui/material';
import classNames from 'classnames';
import SideBarIcon from '../../assets/logo/SideBarIcon';
import useTheme from '../../hooks/ui/useTheme';
import BackIcon from '../../assets/logo/BackIcon';
import { useTranslation } from 'react-i18next';
import { SCREENS } from '../../services/exports/Constants';
import useRouteMatch from '../../hooks/navigation/useRouteMatch';
import useAppMethods from '../../hooks/utility/useAppMethods';

type NavItemType = 'link' | 'button';

interface INavItem {
  label: string;
  to?: string;
  type: NavItemType;
  onClick?: () => void;
  onClicked?: () => void;
  className?: string;
}

interface IDevice {
  items: INavItem[];
}

interface IBack {
  onClick: () => void;
  title: string;
}

const PageHeader = (): JSX.Element => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Headers:PageHeader' });
  const { data } = useInitialData();
  const { company, franchise } = data;
  const { isDesktop } = useScreenType();
  const { generatePath } = useRouter();
  const { categories, generateMenuScrollQuery } = useMenu();
  const routeMatch = useRouteMatch();
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState<boolean>(false);
  const { toggleOrderMethodModal } = useAppMethods();

  const items = [
    {
      label: t('nav_items.menu'),
      to: `${generatePath(SCREENS.HOME_SCREEN)}${generateMenuScrollQuery(
        collect(categories).first()?.id,
        routeMatch(SCREENS.HOME_SCREEN) ? 'smooth' : 'auto',
      )}`,
      type: 'link',
    },
    franchise?.has_multiple_companies && {
      label: t('nav_items.locations'),
      type: 'button',
      onClick: () => toggleOrderMethodModal({ openModal: true }),
    },
    {
      label: t('nav_items.store_info'),
      to: '/store-info',
      type: 'button',
      onClick: () => setShowCompanyDetailsModal(true),
    },
  ].filter(Boolean) as INavItem[];

  const renderCompanyDetailsModal = useMemo<JSX.Element>(() => {
    return (
      <CompanyDetailsModal
        data={company}
        openModal={showCompanyDetailsModal}
        toggleModal={() => setShowCompanyDetailsModal(false)}
      />
    );
  }, [company, showCompanyDetailsModal]);

  const Header = isDesktop ? PageHeader.Desktop : PageHeader.Mobile;

  return (
    <>
      <Header items={items} />
      {renderCompanyDetailsModal}
    </>
  );
};

export default PageHeader;

PageHeader.NavigationLink = ({ to, label, type, className, onClick, onClicked }: INavItem): JSX.Element => {
  if (type === 'link') {
    return (
      <Link to={to} viewTransition className={classNames('shrink-0', className)} onClick={() => onClicked?.()}>
        <h6 className="text-sm text-gray-700">{label}</h6>
      </Link>
    );
  }

  return (
    <button
      onClick={() => {
        onClick();
        onClicked?.();
      }}
      className={classNames('text-left shrink-0', className)}
    >
      <h6 className="text-sm text-gray-700">{label}</h6>
    </button>
  );
};

PageHeader.Desktop = ({ items }: IDevice): JSX.Element => {
  const { navigate } = useRouter();
  return (
    <div className="bg-white shadow-section layout-container">
      <div className="flex lg:px-none px-md py-sm justify-between items-center">
        <div className="cursor-pointer" onClick={() => navigate({ web: { to: SCREENS.HOME_SCREEN } })}>
          <CompanyLogo />
        </div>

        <div className="flex items-center gap-x-lg">
          <div className="flex items-center gap-x-lg">
            {items.map((item, index) => (
              <PageHeader.NavigationLink key={index} {...item} />
            ))}
          </div>
          <div className="flex items-center gap-x-lg">
            <LanguageSelector />
            <UserBadge />
          </div>
        </div>
      </div>
    </div>
  );
};

PageHeader.Mobile = ({ items }: IDevice): JSX.Element => {
  const theme = useTheme();
  const { navigate } = useRouter();
  const [open, setOpen] = useState<boolean>(false);

  const toggleSideBar = (): void => {
    setOpen(!open);
  };

  return (
    <div className="flex px-sm py-sm justify-between items-center h-[60px] bg-white shadow-section">
      <div className="flex items-center gap-x-sm">
        <button onClick={() => setOpen(true)}>
          <SideBarIcon className="h-[16px] w-[16px] stroke-gray-700" color={theme.color.mobile.tertiary} />
        </button>
        <div className="cursor-pointer" onClick={() => navigate({ web: { to: SCREENS.HOME_SCREEN } })}>
          <CompanyLogo className="h-[32px]" />
        </div>
      </div>
      <UserBadge />
      <Drawer
        anchor={'left'}
        open={open}
        onClose={toggleSideBar}
        sx={{
          '& .MuiDrawer-paper': {
            width: '80vw',
          },
        }}
      >
        <div className="flex flex-col [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-gray">
          {items.map((item, index) => (
            <PageHeader.NavigationLink key={index} {...item} onClicked={toggleSideBar} className="p-sm w-full" />
          ))}
          <div className="p-sm w-full">
            <LanguageSelector />
          </div>
        </div>
      </Drawer>
    </div>
  );
};

PageHeader.Back = ({ onClick, title }: IBack): JSX.Element => {
  const theme = useTheme();
  return (
    <button
      onClick={onClick}
      className="flex items-center w-full p-md text-sm z-40 bg-white text-gray-700 lg:p-none lg:w-fit shadow-section"
    >
      <BackIcon className="h-3 !w-3 mr-xs" color={theme.color.mobile.tertiary} />
      {title}
    </button>
  );
};
